const { fetchJSON } = window.BAO.Utils

class StoreSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    this.form =
      this.querySelector('#MarketSwitcherForm') ||
      this.querySelector('form.drw-MarketSwitcher_Form')
    this.select = this.querySelector('[name="country_code"]')
    this._countries = this.loadCountries()

    super.connectedCallback()

    if (this.shouldSkipAutoRedirect) return

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    if (this.countryParam) {
      this.syncStoredCountry(this.countryParam)
      this.selectCountry(this.countryParam)

      const url = new URL(window.location.href)
      url.searchParams.delete('country')
      window.history.replaceState({}, '', url.toString())
    }

    const storedCode = window.localStorage.getItem(this.userLocationName)
    const storedSubfolder = storedCode
      ? this.countries[storedCode.toUpperCase()]?.subfolder
      : null
    const currentSubfolder = this.marketCountry
      ? this.countries[this.marketCountry]?.subfolder
      : null

    if (this.marketCountry && storedSubfolder !== currentSubfolder) {
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
      this.selectCountry(this.marketCountry)
    } else if (!this.noLocationStored) {
      this.syncStoredCountry(storedCode)
      this.selectCountry(storedCode)
    } else if (this.marketCountry) {
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
      this.selectCountry(this.marketCountry)
    } else {
      fetchJSON('/browsing_context_suggestions.json', {
        method: 'GET',
      }).then((res) => {
        const countryCode = res.detected_values.country.handle

        this.handleDetectedCountry(countryCode)
      })
    }

    if (storedCode) {
      Object.entries(this.countries).forEach(([key]) => {
        const isStoredCountry = key.toLowerCase() === storedCode.toLowerCase()
        if (!isStoredCountry) return

        const { site, subfolder } = this.countries[key]
        this.storedUrl = this.createCountryAwareHref(key, site, subfolder)
      })
    }

    if (this.isDefaultMarketVisitor && !this.currentPathSubfolder) {
      const code = this.dataset.currentCountry.toUpperCase()
      window.localStorage.setItem(this.userLocationName, code)
      return
    }

    if (this.storedUrl) {
      const currentUrl = new URL(window.location.href)
      const storedUrl = new URL(this.storedUrl)

      currentUrl.search = ''
      storedUrl.search = ''

      if (currentUrl.href !== storedUrl.href) {
        const redirectUrl = new URL(this.storedUrl)
        const preservedParams = new URL(window.location.href).searchParams

        preservedParams.forEach((value, key) => {
          redirectUrl.searchParams.set(key, value)
        })

        window.location.href = redirectUrl.href
      }
    }
  }

  forceRedirect () {
    if (this.shouldSkipAutoRedirect) return

    fetchJSON('/browsing_context_suggestions.json', {
      method: 'GET',
    }).then((res) => {
      const userCountry = res.detected_values.country.handle.toUpperCase()
      const country = this.countries[userCountry]

      if (!country) return

      const { site, subfolder } = country
      const subfolderSegment = subfolder ? `/${subfolder}` : ''
      const siteUrl = this.selectedSites[site]?.url

      if (!siteUrl) return

      window.location.href = `${siteUrl}${subfolderSegment}${this.cleanPageUrl}`
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onFormSubmit = this.onFormSubmit.bind(this)

    if (this.form) {
      this.listeners.add(this.form, 'submit', this.onFormSubmit, {
        capture: true,
      })
    }
  }

  onFormSubmit (event) {
    if (!this.select) return

    const code = this.select.value?.trim()?.toUpperCase()
    const selectedOption = this.select.selectedOptions?.[0]
    let href = selectedOption?.getAttribute('data-href')?.trim()

    if (!href) {
      const country = this.countries[code]
      if (!country) return

      href = this.createCountryAwareHref(
        code,
        country.site?.trim(),
        country.subfolder?.trim(),
      )
    }

    let targetUrl

    try {
      targetUrl = new URL(href)
    } catch {
      return
    }

    const currentUrl = new URL(window.location.href)
    const targetCompare = new URL(targetUrl.href)
    const currentCompare = new URL(currentUrl.href)

    targetCompare.search = ''
    currentCompare.search = ''

    if (targetCompare.href === currentCompare.href) {
      if (code) window.localStorage.setItem(this.userLocationName, code)
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (code) window.localStorage.setItem(this.userLocationName, code)
    window.location.assign(href)
  }

  handleDetectedCountry (countryCode) {
    if (!countryCode) return

    const normalizedCountryCode = countryCode.toUpperCase()
    const country = this.countries[normalizedCountryCode]

    this.selectCountry(normalizedCountryCode)

    if (!country) {
      this.openMarketSwitcherDrawer()
      return
    }

    if (country.site === this.dataset.store) {
      window.localStorage.setItem(this.userLocationName, normalizedCountryCode)
      return
    }

    this.openMarketSwitcherDrawer()
  }

  selectCountry (countryCode) {
    if (!this.select || !countryCode) return

    const normalizedCountryCode = countryCode.toUpperCase()
    const option = Array.from(this.select.options).find(
      (item) => item.value.toUpperCase() === normalizedCountryCode,
    )

    if (!option) return

    this.select.value = option.value
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  openMarketSwitcherDrawer () {
    if (window.Shopify?.designMode) return

    const openDrawer = () => {
      if (typeof window.drawers?.open === 'function') {
        window.drawers.open('market-switcher')
        return true
      }

      return false
    }

    if (openDrawer()) return

    window.setTimeout(() => {
      openDrawer()
    }, 100)
  }

  loadCountries () {
    const configEl = this.querySelector('[data-store-switcher-el="countries"]')

    if (configEl?.textContent) {
      try {
        const parsed = JSON.parse(configEl.textContent)

        if (parsed && typeof parsed === 'object') {
          window.theme ??= {}
          window.theme.countries = parsed
          return parsed
        }
      } catch {
        /* use window.theme.countries fallback */
      }
    }

    if (window.theme?.countries && typeof window.theme.countries === 'object') {
      return window.theme.countries
    }

    return {}
  }

  syncStoredCountry (countryCode) {
    const normalizedCountryCode = countryCode.toUpperCase()
    if (this.countries[normalizedCountryCode]?.site === this.dataset.store) {
      window.localStorage.setItem(this.userLocationName, normalizedCountryCode)
    }
  }

  createCountryAwareHref (countryCode, site, subfolder) {
    const siteUrl = this.selectedSites[site]?.url?.replace(/\/$/, '')
    if (!siteUrl) return window.location.href

    const pagePath = this.cleanPageUrl.startsWith('/')
      ? this.cleanPageUrl
      : `/${this.cleanPageUrl}`
    const subfolderSegment = subfolder ? `/${subfolder}` : ''
    const targetUrl = new URL(`${siteUrl}${subfolderSegment}${pagePath}`)

    if (targetUrl.origin !== window.location.origin) {
      targetUrl.searchParams.set('country', countryCode)
    }

    return targetUrl.toString()
  }

  get cleanPageUrl () {
    const pageUrl = this.dataset.pageUrl || '/'
    const subfolders = Object.values(this.countries)
      .map((c) => c.subfolder?.trim())
      .filter(Boolean)

    for (const subfolder of subfolders) {
      if (pageUrl === `/${subfolder}`) return '/'
      if (pageUrl.startsWith(`/${subfolder}/`)) {
        return pageUrl.slice(`/${subfolder}`.length)
      }
    }

    return pageUrl
  }

  get shouldSkipAutoRedirect () {
    if (this.dataset.designMode === 'true') return true
    if (window.Shopify?.designMode) return true

    return new URLSearchParams(window.location.search).has('preview_theme_id')
  }

  get currentPathSubfolder () {
    const path = window.location.pathname
    const subfolders = Object.values(this.countries)
      .map((country) => country.subfolder?.trim())
      .filter(Boolean)

    for (const subfolder of subfolders) {
      if (path === `/${subfolder}` || path.startsWith(`/${subfolder}/`)) {
        return subfolder
      }
    }

    return null
  }

  get isDefaultMarketVisitor () {
    const code = this.dataset.currentCountry?.toUpperCase()
    const country = code ? this.countries[code] : null

    return Boolean(country && !country.subfolder?.trim())
  }

  get countryParam () {
    const countryCode = new URLSearchParams(window.location.search).get(
      'country',
    )
    if (!countryCode) return null

    const normalizedCountryCode = countryCode.toUpperCase()
    return this.countries[normalizedCountryCode] ? normalizedCountryCode : null
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !window.Shopify.designMode)
    )
  }

  get userLocationName () {
    return 'STRIKE-BAO-localization-v2'
  }

  get selectedSites () {
    return {
      INT: { url: this.dataset.intUrl },
      AU: { url: this.dataset.auUrl },
      SA: { url: this.dataset.saUrl },
    }
  }

  get marketCountry () {
    const code = this.dataset.currentCountry?.toUpperCase()
    if (!code || !this.countries[code]) return null
    return this.countries[code].subfolder ? code : null
  }

  get countries () {
    return this._countries
  }
}

if (!customElements.get('store-switcher')) {
  customElements.define('store-switcher', StoreSwitcher)
}
